<script setup lang="ts">
import useSecurityChecks from '~/composables/useSecurityChecks'

const route = useRoute()
const slug = route.params.slug as string
const toast = useToast()

// Fetch the specific category from the API
const { data: category } = await useFetch(`/api/categories/${slug}`)

// If category not found, throw 404
if (!category.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Category not found',
    fatal: true,
  })
}

// Email subscription state
const email = ref('')
const loading = ref(false)
const honeypot = ref('')
const router = useRouter()
const emailInput = ref(null)

// Add security checks
const { getSecurityData } = useSecurityChecks()

// Set SEO meta description based on category
useSeoMeta({
  title: computed(() => {
    if (category.value) {
      return `AERO.NOW • ${category.value.icon} ${category.value.name} Newsletter`
    }
    return 'AERO.NOW • Aerospace Newsletter'
  }),
  ogTitle: computed(() => {
    if (category.value) {
      return `AERO.NOW • ${category.value.icon} ${category.value.name} Newsletter`
    }
    return 'AERO.NOW • Aerospace Newsletter'
  }),
  twitterTitle: computed(() => {
    if (category.value) {
      return `AERO.NOW • ${category.value.icon} ${category.value.name} Newsletter`
    }
    return 'AERO.NOW • Aerospace Newsletter'
  }),
  description: computed(() => {
    if (category.value?.slug === 'aerospace') {
      return 'The most significant stories, trends, and insights shaping the aerospace industry and its future.'
    }
    // Default to category description for other categories
    return (
      category.value?.description ||
      'Get curated aerospace industry insights delivered to your inbox.'
    )
  }),
  twitterDescription: computed(() => {
    if (category.value?.slug === 'aerospace') {
      return 'The most significant stories, trends, and insights shaping the aerospace industry and its future.'
    }
    // Default to category description for other categories
    return (
      category.value?.description ||
      'Get curated aerospace industry insights delivered to your inbox.'
    )
  }),
})

onMounted(() => {
  // Access the actual input element inside UInput
  const input = emailInput.value?.$el?.querySelector('input')
  input?.focus()
})

// Updated form submission handler with proper waitlist handling
async function navigateToForm(e: Event) {
  e.preventDefault()

  if (honeypot.value) {
    console.log('Bot detected')
    return
  }

  if (!email.value) {
    toast.add({
      title: 'Error',
      description: 'Please enter your email',
      color: 'red',
    })
    return
  }

  // Start with not resuming flow
  let isResumingFlow = false

  // Check for existing subscriptions via the API first
  const securityData = getSecurityData()
  try {
    // First check if this email exists in our system
    console.log('Checking subscription status for:', email.value)

    const statusCheck = await $fetch('/api/subscribe/check-status', {
      method: 'POST',
      body: {
        email: email.value,
        fingerprint: securityData.fp,
        flow_resumed: false, // Initial check with resumption off
      },
    })

    console.log('Subscription status check result:', statusCheck)

    // If email exists and is not active, we'll set resumption to true
    if (statusCheck.exists) {
      console.log('Email exists in system, checking if resumable:', {
        status: statusCheck.status,
        resumable: statusCheck.resumable,
        source: statusCheck.source,
      })

      // For active subscribers, block them
      if (statusCheck.status === 'active') {
        toast.add({
          title: 'Already subscribed',
          description: 'This email is already active in our system',
          color: 'primary',
        })
        return
      }
      // If this browser already subscribed with a different email, block them
      else if (
        statusCheck.status === 'browser_subscribed' &&
        statusCheck.subscribedEmail &&
        statusCheck.subscribedEmail !== email.value
      ) {
        toast.add({
          title: 'Already subscribed',
          description: `You've already subscribed with a different email`,
          color: 'primary',
        })
        return
      }
      // For pending cases, set isResumingFlow to true to resume their flow
      else if (statusCheck.resumable === true) {
        console.log('Resuming incomplete flow for:', email.value)
        isResumingFlow = true
      }
      // For explicitly completed/waitlisted subscriptions, block resumption
      else if (
        statusCheck.status === 'completed' ||
        statusCheck.status === 'waitlisted'
      ) {
        toast.add({
          title: 'Already subscribed',
          description: `This email has already completed the signup process`,
          color: 'primary',
        })
        return
      }
      // Any other case, try resuming anyway
      else {
        console.log('Status unclear, attempting resumption for:', email.value)
        isResumingFlow = true
      }
    }

    // Continue with normal flow or with resumption
  } catch (error) {
    console.error('Error checking subscription status:', error)
    // Continue anyway as the main endpoint will also check
  }

  loading.value = true
  try {
    // Use the waitlist/join endpoint directly to FORCE waitlist flow
    const response = await $fetch('/api/waitlist/join', {
      method: 'POST',
      body: {
        email: email.value,
        codeType: 'none', // Explicitly no code type
        honeypot: honeypot.value,
        security: securityData, // Add security data
        flow_resumed: isResumingFlow, // Use the determined value
      },
    })

    console.log('Waitlist response:', response)

    // Process the response
    handleWaitlistResponse(response)
  } catch (error) {
    console.error('Subscription error:', error)
    loading.value = false

    // Check for subscription with different email
    if (error.status === 409 && error.data?.subscribedEmail) {
      toast.add({
        title: 'Already subscribed',
        description: `You've already subscribed`,
        color: 'primary',
      })
    } else {
      toast.add({
        title: 'Error',
        description: error.data?.message || 'Failed to subscribe',
        color: 'red',
      })
    }
  }
}

// Resumption response type
interface BaseAPIResponse {
  success: boolean
  [key: string]: unknown
}

// Waitlist response extends the base response
interface WaitlistResponse extends BaseAPIResponse {
  resuming?: boolean
  waitlistPosition?: number
  referralToken?: string
  uuid?: string
  skipCode?: string
  skippedWaitlist?: boolean
  position?: number
}

function handleWaitlistResponse(response: unknown) {
  // Validate and cast response to the proper type
  const waitlistResponse = response as WaitlistResponse

  // TypeScript guard to check success property
  if (
    waitlistResponse &&
    typeof waitlistResponse === 'object' &&
    'success' in waitlistResponse &&
    waitlistResponse.success
  ) {
    // Flow identifier now tracked server-side via KV, no localStorage needed

    // Prepare common query parameters
    const baseQuery = {
      email: email.value,
      category: category.value.slug,
      isMain: 'false',
      flow: 'category', // Add flow identifier in query param instead of localStorage
    }

    // Route based on whether waitlist was resumed
    if ('resuming' in waitlistResponse && waitlistResponse.resuming) {
      // We're resuming an existing flow
      console.log('Resuming existing flow with data:', waitlistResponse)

      if (
        'waitlistPosition' in waitlistResponse &&
        waitlistResponse.waitlistPosition
      ) {
        // This was a waitlist signup that wasn't completed
        router.push({
          path: '/subscribe',
          query: {
            ...baseQuery,
            position: String(waitlistResponse.waitlistPosition),
            referralToken:
              'referralToken' in waitlistResponse
                ? String(waitlistResponse.referralToken)
                : '',
            uuid:
              'uuid' in waitlistResponse ? String(waitlistResponse.uuid) : '',
          },
        })
      } else {
        // This was a direct signup that wasn't completed
        router.push({
          path: '/subscribe',
          query: {
            ...baseQuery,
            uuid:
              'uuid' in waitlistResponse ? String(waitlistResponse.uuid) : '',
            skipCode:
              'skipCode' in waitlistResponse
                ? String(waitlistResponse.skipCode)
                : '',
          },
        })
      }
    } else if (
      'skippedWaitlist' in waitlistResponse &&
      waitlistResponse.skippedWaitlist
    ) {
      router.push({
        path: '/subscribe',
        query: {
          ...baseQuery,
          skippedWaitlist: 'true',
          uuid: 'uuid' in waitlistResponse ? String(waitlistResponse.uuid) : '',
          skipCode:
            'skipCode' in waitlistResponse
              ? String(waitlistResponse.skipCode)
              : '',
        },
      })
    } else {
      router.push({
        path: '/subscribe',
        query: {
          ...baseQuery,
          position:
            'position' in waitlistResponse
              ? String(waitlistResponse.position)
              : '0',
          referralToken:
            'referralToken' in waitlistResponse
              ? String(waitlistResponse.referralToken)
              : '',
        },
      })
    }
  } else {
    // Handle unsuccessful response
    loading.value = false
    toast.add({
      title: 'Error',
      description: 'Failed to process subscription',
      color: 'red',
    })
  }
}
</script>

<template>
  <UContainer>
    <UPage>
      <ULandingSection class="py-8">
        <div class="max-w-md mx-auto text-center">
          <!-- Category Icon & Name -->
          <span class="text-4xl mb-4 block">{{ category.icon }}</span>
          <h1 class="text-4xl font-bold mb-4">{{ category.name }}</h1>

          <!-- Hooks -->
          <!-- <h2 class="text-2xl font-semibold mb-3">{{ hooks.main }}</h2> -->
          <p class="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            {{ category.description }}
          </p>

          <!-- Email Subscription Form -->
          <ULandingSection class="w-full mx-auto py-0 pt-2 sm:py-0">
            <template #description>
              <span
                >Busy? Get aerospace insights in just<br />
                <strong>5 minutes per day 🚀</strong></span
              >
            </template>
            <template #headline>
              <form class="max-w-lg mx-auto" @submit.prevent="navigateToForm">
                <UFormGroup :ui="{ container: 'mt-3' }">
                  <UInput
                    ref="emailInput"
                    v-model="email"
                    type="email"
                    placeholder="Enter your email"
                    :ui="{ icon: { trailing: { pointer: '' } } }"
                    required
                    size="xl"
                    autocomplete="off"
                    class="w-full"
                    input-class="rounded-full w-[300px] sm:w-[350px] border-2 border-primary focus:ring-primary focus:border-primary focus:outline-none text-truncate pr-[125px]"
                  >
                    <template #trailing>
                      <UButton
                        type="submit"
                        size="xs"
                        color="primary"
                        :label="loading ? 'Subscribing' : 'Subscribe Free'"
                        :loading="loading"
                      />
                    </template>
                  </UInput>
                </UFormGroup>
                <!-- Honeypot field - hidden from real users -->
                <input
                  v-model="honeypot"
                  type="text"
                  name="contact-preference"
                  aria-hidden="true"
                  class="absolute top-0 left-0 h-0 w-0 -z-50 opacity-0 pointer-events-none"
                  tabindex="-1"
                  autocomplete="off"
                />
              </form>

              <p class="text-gray-500 mt-2 font-normal text-xs">
                <UIcon
                  name="i-heroicons-shield-check"
                  class="text-gray-400 translate-y-[1px]"
                />
                No spam. Unsubscribe at any time in one click.
              </p>
            </template>
          </ULandingSection>
        </div>

        <!-- Category Description -->
        <!-- <div class="mt-12 max-w-2xl mx-auto text-center">
          <p class="text-lg text-gray-600 dark:text-gray-400">
            {{ category.description }}
          </p>
        </div> -->
      </ULandingSection>
    </UPage>
  </UContainer>
</template>
